import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../config';
import { s } from './TissStyles';
import { statusColors } from './TissConstants';
import { formatDate, formatDateTime } from './TissHelpers';
import { StatusBadge, PillFilters, useToast } from './TissShared';
import { baixarXmlLote, Paginacao } from './TissFluxo';

const LIMIT = 30;
const td = { ...s.td, padding: '7px 10px', verticalAlign: 'top' };
const th = { ...s.th, padding: '8px 10px' };
const mono = { fontFamily: 'monospace', fontSize: '12px' };
const sub = { fontSize: '11px', color: '#888' };

// Lotes = etapas 5 (em lote, ainda não enviado) e 6 (enviado, com protocolo e situação na UNIMED).
// A coluna "Valor" saiu: tiss_lotes.valor_total guarda o valor de UMA guia (bug documentado na
// memória do projeto) e mostrava 5× menos que o apresentado.
export default function TabLotes({ dataInicio, dataFim }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [status, setStatus] = useState('todos');
  const [buscaInput, setBuscaInput] = useState('');
  const [busca, setBusca] = useState('');
  const [page, setPage] = useState(1);
  const [lotes, setLotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [contagens, setContagens] = useState({});
  const [loading, setLoading] = useState(true);
  const [ocupado, setOcupado] = useState(null);
  const [showCriar, setShowCriar] = useState(false);
  const [disponiveis, setDisponiveis] = useState([]);
  const [selecionadas, setSelecionadas] = useState([]);

  useEffect(() => { setPage(1); }, [status, busca, dataInicio, dataFim]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const periodo = {}; if (dataInicio) periodo.dataInicio = dataInicio; if (dataFim) periodo.dataFim = dataFim;
      const params = { page, limit: LIMIT, ...periodo };
      if (status !== 'todos') params.status = status;
      if (busca) params.search = busca;
      const [lista, cont] = await Promise.all([
        axios.get(`${API_URL}/tiss/lotes`, { params }),
        axios.get(`${API_URL}/tiss/lotes/contagens`, { params: periodo }),
      ]);
      setLotes(lista.data.data || []);
      setTotal(lista.data.total || 0);
      setContagens(cont.data || {});
    } catch (err) { console.error('Erro lotes:', err); toast.error('Erro ao carregar lotes'); }
    finally { setLoading(false); }
  }, [page, status, busca, dataInicio, dataFim]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const acao = async (l, a) => {
    if (a === 'enviar' && !window.confirm(`Enviar o lote ${l.numero_lote} (${l.total_guias} guias) para a UNIMED?`)) return;
    if (a === 'reenviar' && !window.confirm(`Reenviar o lote ${l.numero_lote}?`)) return;
    setOcupado(`${l.id}:${a}`);
    try {
      const res = await axios.post(`${API_URL}/tiss/lotes/${l.id}/${a}`);
      if (a === 'status') {
        const st = res.data.lote?.status;
        toast.info(`Situação do lote: ${res.data.lote?.situacao_operadora_label || statusColors[st]?.label || st || 'sem alteração'}`);
      } else toast.success(a === 'enviar' ? 'Lote enviado' : 'Lote reenviado');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro'); }
    finally { setOcupado(null); }
  };

  const abrirCriar = async () => {
    try {
      const res = await axios.get(`${API_URL}/tiss/guias`, { params: { etapa: 'realizada', limit: 300 } });
      setDisponiveis((res.data.data || []).filter(g => !g.lote_id));
      setSelecionadas([]);
      setShowCriar(true);
    } catch { toast.error('Erro ao buscar guias realizadas'); }
  };
  const criar = async () => {
    try {
      await axios.post(`${API_URL}/tiss/lotes/criar`, { guia_ids: selecionadas });
      toast.success('Lote criado');
      setShowCriar(false);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro ao criar lote'); }
  };

  const pills = [
    { id: 'pendente', label: '5 · Montados, não enviados', count: contagens.pendentes || 0, color: '#6a1b9a' },
    { id: 'enviado', label: '6 · Enviados', count: contagens.enviados || 0, color: '#00695c' },
    { id: 'aceito', label: 'Aceitos', count: contagens.aceitos || 0, color: '#2e7d32' },
    { id: 'rejeitado', label: 'Rejeitados', count: contagens.rejeitados || 0, color: '#c62828' },
    { id: 'erro', label: 'Erro', count: contagens.erros || 0, color: '#c62828' },
    { id: 'todos', label: 'Todos', count: contagens.total || 0, color: '#555' },
  ];

  return (
    <>
      <PillFilters items={pills} active={status} onChange={setStatus} style={{ marginBottom: '10px' }} />
      <div style={{ ...s.filtersRow, padding: '10px 14px', alignItems: 'center' }}>
        <input style={{ ...s.input, flex: 1, minWidth: '220px' }} value={buscaInput} placeholder="Buscar nº do lote ou protocolo UNIMED (Enter)"
          onChange={e => setBuscaInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') setBusca(buscaInput.trim()); }} />
        <button style={s.btnPrimary} onClick={() => setBusca(buscaInput.trim())}>Buscar</button>
        {busca && <button style={s.btnSecondary} onClick={() => { setBusca(''); setBuscaInput(''); }}>Limpar</button>}
        <button style={{ ...s.btnSecondary, marginLeft: 'auto' }} onClick={load}>Atualizar</button>
        <button style={s.btnPrimary} onClick={abrirCriar}>Montar lote</button>
      </div>

      <div style={{ ...s.card, padding: '6px 10px 10px' }}>
        {loading ? <div style={{ textAlign: 'center', padding: '24px', color: '#999' }}>Carregando…</div>
        : lotes.length === 0 ? <div style={s.emptyState}><p style={s.emptyText}>Nenhum lote nesse filtro e período</p></div> : (
          <>
          <table style={s.table}>
            <thead><tr>
              <th style={th}>Lote</th><th style={th}>Guias</th><th style={th}>Enviado em</th><th style={th}>Protocolo UNIMED</th>
              <th style={th}>Situação</th>
              <th style={th} title="Será preenchido com a leitura do portal SGU-Card">Situação no SGU</th>
              <th style={{ ...th, width: '270px' }}></th>
            </tr></thead>
            <tbody>{lotes.map(l => (
              <tr key={l.id}>
                <td style={td}><span style={{ ...mono, fontWeight: 600, color: '#1976d2', cursor: 'pointer' }} onClick={() => navigate(`/tiss-unimed/lote/${l.id}`)}>{l.numero_lote}</span>
                  <div style={sub}>montado {formatDate(l.created_at)}{l.origem_lote_id ? ' · reenvio' : ''}{l.substituido_por_lote_id ? ' · substituído' : ''}</div></td>
                <td style={td}>{l.total_guias}</td>
                <td style={td}>{l.enviado_em ? formatDateTime(l.enviado_em) : <span style={{ color: '#bbb' }}>—</span>}</td>
                <td style={td}><span style={mono}>{l.numero_protocolo_operadora || '—'}</span></td>
                <td style={td}><StatusBadge status={l.status} />
                  {l.situacao_operadora_label && <div style={sub}>{l.situacao_operadora_label}{l.situacao_consultada_em ? ` (consultado ${formatDate(l.situacao_consultada_em)})` : ''}</div>}
                  {l.ultimo_erro && <div style={{ ...sub, color: '#c62828', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.ultimo_erro}>{l.ultimo_erro}</div>}</td>
                <td style={{ ...td, color: '#bbb' }}>—</td>
                <td style={td}><div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                  <button style={s.btnSmall} onClick={() => navigate(`/tiss-unimed/lote/${l.id}`)}>Ver</button>
                  {l.status === 'pendente' && <button style={{ ...s.btnSmall, color: '#1976d2', borderColor: '#1976d2' }} disabled={!!ocupado} onClick={() => acao(l, 'enviar')}>{ocupado === `${l.id}:enviar` ? 'Enviando…' : 'Enviar'}</button>}
                  {l.status === 'enviado' && <button style={s.btnSmall} disabled={!!ocupado} onClick={() => acao(l, 'status')}>{ocupado === `${l.id}:status` ? 'Consultando…' : 'Consultar situação'}</button>}
                  {['erro', 'rejeitado'].includes(l.status) && <button style={{ ...s.btnSmall, color: '#e65100', borderColor: '#e65100' }} disabled={!!ocupado} onClick={() => acao(l, 'reenviar')}>{ocupado === `${l.id}:reenviar` ? 'Reenviando…' : 'Reenviar'}</button>}
                  {l.tem_xml && <button style={s.btnSmall} onClick={() => baixarXmlLote(l, toast)}>XML</button>}
                </div></td>
              </tr>))}
            </tbody>
          </table>
          <Paginacao page={page} limit={LIMIT} total={total} onPage={setPage} />
          </>
        )}
      </div>

      {showCriar && (
        <div style={s.modal} onClick={() => setShowCriar(false)}>
          <div style={{ ...s.modalContent, maxWidth: '720px' }} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>Montar lote de faturamento</div>
            <p style={{ fontSize: '13px', color: '#666', margin: '0 0 10px' }}>Guias realizadas que ainda não estão em lote ({disponiveis.length})</p>
            {disponiveis.length === 0 ? <div style={s.emptyState}><p style={s.emptyText}>Nenhuma guia realizada disponível</p></div> : (
              <div style={{ maxHeight: '340px', overflowY: 'auto' }}>
                <table style={s.table}>
                  <thead><tr>
                    <th style={th}><input type="checkbox" checked={selecionadas.length === disponiveis.length}
                      onChange={e => setSelecionadas(e.target.checked ? disponiveis.map(g => g.id) : [])} /></th>
                    <th style={th}>Atendimento</th><th style={th}>Paciente</th><th style={th}>Guia UNIMED</th><th style={th}>Criada</th>
                  </tr></thead>
                  <tbody>{disponiveis.map(g => (
                    <tr key={g.id}>
                      <td style={td}><input type="checkbox" checked={selecionadas.includes(g.id)}
                        onChange={e => setSelecionadas(p => e.target.checked ? [...p, g.id] : p.filter(x => x !== g.id))} /></td>
                      <td style={td}><span style={mono}>{g.solicitacao_local}/{g.solicitacao_protocolo}</span></td>
                      <td style={td}>{g.paciente_nome}</td>
                      <td style={td}><span style={mono}>{g.numero_guia_operadora || '—'}</span></td>
                      <td style={td}>{formatDate(g.created_at)}</td>
                    </tr>))}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' }}>
              <button style={s.btnSecondary} onClick={() => setShowCriar(false)}>Cancelar</button>
              <button style={s.btnPrimary} disabled={!selecionadas.length} onClick={criar}>Montar lote ({selecionadas.length} guias)</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
